// --- React
import React from 'react';
// --- UI Components
import {DetailsModal} from '../../DetailsModal';
import {EditCommentForm} from './edit-form';
import Button from '@dtci/spellbook/Button';
// --- Redux
import {useSelector} from 'react-redux';
import {useCommentsActions} from '../../../store/comments/useComments';
// --- API
import {datastageApi} from '../../../api';
// --- Utilities
import {filterCriteria} from '../../../utils/filterCriteria';
// --- Hooks
import {useLoading} from '../../../utils/hooks/useLoading';
import {useToastNotification} from '../../ToastNotifications';
import {useDeleteDialog} from '../../dialogs/dialogs/delete';
import {usePermissions} from '../../../utils/permissions';

/**
 * EditCommentModal Component
 *
 * This component is responsible for rendering the modal used to edit comments.
 * It includes a form for entering comment details and provides options to save or cancel.
 * It also handles the logic for saving the comment and updating the comment list.
 *
 * @component
 * @returns {JSX.Element} The rendered EditCommentModal component.
 */
export function EditCommentModal() {
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {openToast} = useToastNotification();
    const {isEditModalOpen, comment, commentProduct, filterForm, commentRange, commentSort} = useSelector(
        (s) => s.comments,
    );
    const {openDeleteDialog} = useDeleteDialog();
    const {updateComments} = useCommentsActions();
    const {isLegalUser, isLegalAdmin, isProductCommentsAdmin} = usePermissions();

    // Function to handle the close action of the edit comment modal
    const onEditCommentClose = () => {
        updateComments({
            isEditModalOpen: false,
        });
    };

    // Function to handle the save action of the comment
    const handleSaveComment = async () => {
        setLoadingTrue();
        try {
            if (!comment) return;

            const res = await datastageApi.saveComment(comment, commentProduct);

            if (res?.status === 200) {
                const commentList = await datastageApi.comments(
                    filterCriteria(filterForm, commentProduct, 'comments'),
                    commentRange,
                    commentSort,
                );

                updateComments({
                    comment: res?.data,
                    comments: commentList?.content,
                });
                openToast('Comment saved successfully', 'success');
            }
        } catch (error) {
            console.error('Error saving comment:', error);
            openToast('Error saving comment', 'error');
        } finally {
            setLoadingFalse();
        }
    };

    // Function to handle the delete action of the comment
    const handleDeleteComment = async () => {
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to delete this comment?`,
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();

                try {
                    if (!comment) return;

                    const res = await datastageApi.deleteComment(comment?.id);

                    if (res?.success === true) {
                        const commentList = await datastageApi.comments(
                            filterCriteria(filterForm, commentProduct, 'comments'),
                            commentRange,
                            commentSort,
                        );

                        updateComments({
                            comment: [],
                            comments: commentList?.content,
                            isEditModalOpen: false,
                            totalPages: commentList?.totalPages,
                            totalItems: commentList?.totalElements,
                            currentPage: commentList?.pageable.pageNumber,
                        });

                        openToast('Comment deleted successfully', 'success');
                    } else {
                        openToast('Error deleting comment', 'error');
                    }
                } catch (error) {
                    console.error('Error deleting comment:', error);
                    openToast('Error deleting comment', 'error');
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    return (
        <DetailsModal
            isOpen={isEditModalOpen}
            onClose={onEditCommentClose}
            modalHeader="Edit Comment"
            footer={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {(isLegalAdmin || isProductCommentsAdmin) && (
                        <Button
                            iconType="fa-regular fa-trash-can"
                            sizeType="small"
                            styleType="link"
                            onClick={handleDeleteComment}
                        >
                            Delete
                        </Button>
                    )}
                    <div>
                        <Button
                            iconType="fa-regular fa-close"
                            sizeType="small"
                            styleType="link"
                            onClick={onEditCommentClose}
                        >
                            Cancel
                        </Button>
                        {(isLegalAdmin || isProductCommentsAdmin) && (
                            <Button
                                iconType="fa-regular fa-floppy-disk"
                                sizeType="small"
                                styleType="link"
                                onClick={handleSaveComment}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            <EditCommentForm />
        </DetailsModal>
    );
}